'use client';

import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import { KeyboardEvent, MouseEvent, useCallback, useContext } from 'react';
import { CartContext } from '@/entities/cart';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { Address } from '@/shared/model/Address';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { VenuesQuery } from '../api/queries/VenuesQuery';
import { SET_AS_DEFAULT_VENUE_SEARCH_PARAM } from '../constants';

type Options = {
    deliveryType: DeliveryType | null;
    address: Address | null;
};

/**
 * Returns a callback that builds venue links and their click and keyboard handlers.
 * The generated URL carries the selected delivery details and initializes the venue-router session.
 */
export const useGetVenueHrefAttributes = () => {
    const config = useContext(AppConfigContext);
    const { formMethods } = useContext(CartContext);
    const router = useRouter();
    const pathname = usePathname();

    const venuesQuery = useQuery({
        ...VenuesQuery,
        select: (venues) => ({
            mappedVanues: Object.fromEntries(venues.map((venue) => [venue.apiPartnerSettingsId, venue])),
            venues,
        }),
    });
    const getVenueHrefAttributes = useCallback(
        (apiPartnerSettingsId: string, options?: Options) => {
            const searchParams = new URLSearchParams();
            if (options?.address && options.deliveryType !== DeliveryType.PICKUP) {
                searchParams.append('street', options.address.street);
                searchParams.append('city', options.address.city);
                searchParams.append('postCode', options.address.postCode);
                searchParams.append('country', options.address.country);
                searchParams.append('lat', options.address.lat);
                searchParams.append('lon', options.address.lon);
            }
            if (options?.deliveryType) {
                searchParams.append('deliveryType', options.deliveryType);
            }
            searchParams.append(SET_AS_DEFAULT_VENUE_SEARCH_PARAM, '1');
            const venueUrl = venuesQuery.data?.mappedVanues[apiPartnerSettingsId].url;
            const href = `${venueUrl}?${searchParams.toString()}`;

            const handler = (e?: MouseEvent | KeyboardEvent) => {
                if (config.apiPartnerSettingsId !== apiPartnerSettingsId) {
                    return;
                }
                e?.preventDefault();
                ModalService.hideAll();
                router.replace(`${pathname}?${searchParams.toString()}`, { scroll: true });
            };

            return {
                href,
                onClick: handler,
                onKeyDown: (e: KeyboardEvent) => {
                    if (e.key !== 'Enter' || e.shiftKey) {
                        return;
                    }
                    handler(e);
                },
            };
        },
        [venuesQuery.data, config.apiPartnerSettingsId, formMethods, router, pathname],
    );

    return getVenueHrefAttributes;
};
